import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, Inject, Input, OnChanges, OnDestroy, OnInit, Optional, PLATFORM_ID, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';

import { GetCollectionsQuery, GetCollectionsQueryVariables } from '../../../common/generated-types';
import { GET_COLLECTIONS } from '../../../common/graphql/documents.graphql';
import { DataService } from '../../providers/data/data.service';
import { AppCurrency, CurrencyService } from '../../providers/currency/currency.service';
import { AppLanguage, LanguageService } from '../../providers/language/language.service';
import { GET_EXCHANGE_RATE } from '../../../shared/pipes/get-exchange-rate.graphql';
import { StateService } from '../../providers/state/state.service';
import { arrayToTree, RootNode, TreeNode } from '../collections-menu/array-to-tree';


type CollectionItem = GetCollectionsQuery['collections']['items'][number];

@Component({
    selector: 'vsf-collections-menu-mobile',
    templateUrl: './collections-menu-mobile.component.html',
    styleUrls: ['./collections-menu-mobile.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CollectionsMenuMobileComponent implements OnInit, OnChanges, OnDestroy {
    @HostBinding('class.visible')
    @Input() visible = false;

    private bodyScrollLockY = 0;

    collectionTree$: Observable<RootNode<CollectionItem>>;
    sectorCollections$: Observable<CollectionItem[]>;
    sypAvailable$: Observable<boolean>;
    selected0: string | null = null;
    selected1: string | null = null;

    constructor(private router: Router,
                private stateService: StateService,
                private dataService: DataService,
                readonly languageService: LanguageService,
                readonly currencyService: CurrencyService,
                @Optional() @Inject(DOCUMENT) private document: Document | null,
                @Inject(PLATFORM_ID) private platformId: object) {
        this.sypAvailable$ = this.dataService.query<{ exchangeRate: number }>(GET_EXCHANGE_RATE).pipe(
            map(data => Number(data?.exchangeRate) > 0),
            catchError(() => of(false)),
            tap(available => {
                if (!available && this.currencyService.currentCurrency === 'SYP') {
                    this.currencyService.setCurrency('USD');
                }
            }),
            shareReplay(1),
        );
    }

    setLanguage(lang: AppLanguage): void {
        this.languageService.setLanguage(lang);
    }

    setCurrency(currency: AppCurrency): void {
        this.currencyService.setCurrency(currency);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ('visible' in changes && isPlatformBrowser(this.platformId)) {
            this.setBodyScrollLocked(this.visible);
        }
    }

    ngOnDestroy(): void {
        if (isPlatformBrowser(this.platformId)) {
            this.setBodyScrollLocked(false);
        }
    }

    ngOnInit() {
        const allCollections$ = this.dataService.query<GetCollectionsQuery, GetCollectionsQueryVariables>(GET_COLLECTIONS, {
            options: { take: 50 },
        });

        this.collectionTree$ = allCollections$.pipe(
            map(data => arrayToTree(data.collections.items.filter(c => !(c as any).customFields?.isSector))),
        );

        this.sectorCollections$ = allCollections$.pipe(
            map(data => data.collections.items.filter(c => (c as any).customFields?.isSector)),
        );
    }

    onL0Click(event: TouchEvent, collection: TreeNode<CollectionItem>) {
        this.expandOrNavigate(0, event, collection);
    }

    onL1Click(event: TouchEvent, collection: TreeNode<CollectionItem>) {
        this.expandOrNavigate(1, event, collection);
    }

    close() {
        this.stateService.setState('mobileNavMenuIsOpen', false);
    }

    private setBodyScrollLocked(lock: boolean): void {
        const body = this.document?.body;
        const docEl = this.document?.documentElement;
        if (!body || !docEl) {
            return;
        }
        if (lock) {
            this.bodyScrollLockY = this.document!.defaultView?.scrollY ?? 0;
            body.style.overflow = 'hidden';
            body.style.position = 'fixed';
            body.style.top = `-${this.bodyScrollLockY}px`;
            body.style.left = '0';
            body.style.right = '0';
            body.style.width = '100%';
            docEl.style.overflow = 'hidden';
        } else {
            body.style.removeProperty('overflow');
            body.style.removeProperty('position');
            body.style.removeProperty('top');
            body.style.removeProperty('left');
            body.style.removeProperty('right');
            body.style.removeProperty('width');
            docEl.style.removeProperty('overflow');
            this.document?.defaultView?.scrollTo(0, this.bodyScrollLockY);
        }
    }

    private expandOrNavigate(level: 0 | 1, event: TouchEvent, collection: TreeNode<CollectionItem>) {
        if (collection.children.length && this.selected1 !== collection.id) {
            if (level === 0) {
                this.selected0 = collection.id;
                this.selected1 = null;
            } else {
                this.selected1 = collection.id;
            }
            event.preventDefault();
            event.stopImmediatePropagation();
        } else {
            this.router.navigate(['/category/', collection.slug]);
            this.close();
        }
    }
}
